import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Button,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  WorkspacePremium as BadgeIcon,
  AccessTime as ClockIcon,
  ArrowBack as ArrowBackIcon,
  InfoOutlined as InfoOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import PlanSelector from './PlanSelector';
import TrialFeaturesDialog from './TrialFeaturesDialog';
import TrialOptionsDialog from './TrialOptionsDialog';
import BasicFeaturesDialog from './BasicFeaturesDialog';
import CreditUsageCard from './CreditUsageCard';
import CreditPackDialog from './CreditPackDialog';
import SuccessDialog from './SuccessDialog';
import RichMessage from './RichMessage';
import CreditBreakdown from './CreditBreakdown';
import CreditExpiryBanner from './CreditExpiryBanner';
import { getPlanDisplayName } from '../utils/planUtils';
import { useCreditBalances } from '../hooks/useCreditBalances';
import { creditsSummaryMessage, DEFAULT_PRICING } from '../utils/creditRates';
import { usePieceEstimate } from '../hooks/usePieceEstimate';
import {
  activityAmount, activityCredits, hasAmount, hasCredits, isDecrease,
} from '../utils/billingHistory';
import {
  CREDITS_PATH, PLANS_PATH, PLANS_SCROLLED_PATH, PLANS_SCROLL_TARGET,
} from '../utils/routes';
import {
  cancellationConfirmedMessage, reactivationConfirmedMessage,
  CANCELLATION_CONFIRMED_TITLE, REACTIVATION_CONFIRMED_TITLE,
  DOWNGRADE_SCHEDULED_TITLE,
  creditPurchaseConfirmedMessage, CREDIT_PURCHASE_CONFIRMED_TITLE,
  subscriptionStartedMessage, SUBSCRIPTION_STARTED_TITLE,
  freePlanNextSteps, FREE_PLAN_ACTION, activePlanSubtitle,
  PENDING_CREDIT_PURCHASE_KEY, PLAN_OPTION_TARGET, downgradeToBasicDialog,
} from '../utils/subscriptionCopy';
import {
  deriveCancellationBanner, derivePendingChange, deriveStatusChip, derivePlanActions,
  derivePlanCycleChip, pendingChangeMessage, PENDING_CHANGE_LABEL,
  cancellationScheduledMessage, CANCELLATION_SCHEDULED_LABEL, isLiveSubscription,
} from '../utils/subscriptionState';

// ── Design tokens ──────────────────────────────────────────────────────────
const ORANGE = '#E55432';
const ORANGE_LIGHT = '#FEF0EA';
const TEAL = '#1C7C73';
const TEAL_LIGHT = '#E8F3F1';

/**
 * The status chip's colours, keyed by the tone deriveStatusChip returns.
 *
 * Anything not listed falls back to orange at the call site, so a tone added
 * later still renders — it just does not get its own colour until it is given
 * one here. `muted` exists for the Basic plan: a state the account is
 * legitimately in, which orange would misreport as something to worry about.
 */
const STATUS_CHIP_TONES = {
  positive: { bg: TEAL_LIGHT, color: TEAL },
  muted: { bg: '#F0F0EA', color: '#6B6F68' },
  warning: { bg: ORANGE_LIGHT, color: ORANGE },
  danger: { bg: ORANGE_LIGHT, color: ORANGE },
};
const TEXT = '#1A1A1A';
const TEXT_MUTED = '#8A8F85';
const ORANGE_GRADIENT = 'linear-gradient(135deg, #BF3A1A 0%, #E86340 100%)';

/**
 * The three button treatments this page uses, keyed by the `variant` that
 * derivePlanActions puts on each action.
 *
 * Named once because the emphasis is a decision, not a style: `primary` is "the
 * thing to do", `quiet` is "available, not invited". Ten inline sx blocks in
 * this file already say `textTransform: 'none'` in ten places; three more
 * copies of the same four properties is how they start to disagree.
 */
const BUTTON_VARIANTS = {
  // Orange fill, white text.
  primary: {
    background: ORANGE_GRADIENT,
    color: '#FFFFFF',
    boxShadow: '0 4px 14px rgba(190,58,26,0.30)',
    '&:hover': { background: ORANGE_GRADIENT, filter: 'brightness(1.06)' },
  },
  // White fill, orange text and outline.
  secondary: {
    bgcolor: '#FFFFFF',
    color: ORANGE,
    border: `1px solid ${ORANGE}`,
    '&:hover': { bgcolor: ORANGE_LIGHT },
  },
  // White fill, grey text and outline — the least insistent control on the card.
  quiet: {
    bgcolor: '#FFFFFF',
    color: TEXT_MUTED,
    border: '1px solid #DDE0D8',
    '&:hover': { bgcolor: '#F8F8F4' },
  },
  // White fill, teal text and outline. This is the CREDITS action, and it
  // matches the Add Credits button on the dashboard so the same control looks
  // the same on both screens. It is outlined where the dashboard's is not,
  // because there it sits on the teal credits card and here on a white one —
  // a plain white button on white has no edge at all.
  credits: {
    bgcolor: '#FFFFFF',
    color: TEAL,
    border: `1px solid ${TEAL}`,
    '&:hover': { bgcolor: TEAL_LIGHT },
  },
};

/** Shared geometry, so the three variants differ only in colour. */
/**
 * The Account Activity grid, shared by the header and every row.
 *
 * Defined once because two copies of a column template is how a header stops
 * lining up with the table under it. The gap is deliberately generous: at the
 * previous 8px the numbers ran into the status beside them, so "+140" and
 * "$0.00" and "Succeeded" read as one crowded block rather than three columns.
 */
const ACTIVITY_GRID = {
  display: 'grid',
  gridTemplateColumns: '124px minmax(0, 1fr) 96px 104px 108px',
  columnGap: 3,
};

/**
 * The "Receipt" line under a row's status.
 *
 * Shared by the real link and the hidden placeholder that holds its space on
 * rows without one, so the two cannot drift apart and reintroduce the uneven
 * row heights this exists to prevent.
 */
const ACTIVITY_RECEIPT_SX = {
  display: 'block',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: 11,
  color: ORANGE,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
};

const BUTTON_BASE = {
  borderRadius: '10px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: 14,
  px: 3.5,
  py: 1.25,
  textTransform: 'none',
  whiteSpace: 'nowrap',
};

const formatDisplayPlanName = (name, slug) => {
  if (!name && !slug) return '';
  return getPlanDisplayName({ name, slug });
};

const CreditManagement = ({ onTabChange }) => {
  // MERGE (IfrahWorks) — refreshUserData is hers: loadCreditData() only refreshes
  // this component's state, so without it the header plan/trial badge in Dashboard
  // keeps rendering the pre-change plan_slug until a full page reload.
  const { userData, refreshUserData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // BUG 14 — the single credits source, shared with Overview and Websites.
  // BUG 12 — `detail` carries real consumption from credit_transactions.
  const {
    total: creditsTotal,
    usage,
    // The live price table, so the balance wording uses what the server
    // actually charges rather than a local copy.
    pricing,
    // What is on sale, with real prices — see CreditPackagePicker.
    packages,
    // The balance split into batches that share an expiry date.
    lots,
    // OLD: balance — `{ total, plan, purchased, … }`, read only for `plan`,
    // which the credit-pack dialog used to name. The dialog now names the
    // whole balance (creditsTotal above), so nothing here reads it.
    // balance,
    refresh: refreshCredits,
  } = useCreditBalances(userData?.id);
  // "≈ N pieces" — from the server's one estimate endpoint, shared with the plugin.
  const creditPieces = usePieceEstimate(creditsTotal);
  // The breakdown starts collapsed: the headline answers the question people
  // arrive with, and the rows answer the follow-up.
  const [balanceOpen, setBalanceOpen] = useState(false);
  // The disclosure control, so the tooltip's "Which credits are used first"
  // can bring it into view. That link used to route to the Quick Start Guide,
  // which is a different page answering a different question; the answer it
  // promises — which batch goes first, and when — is the panel below.
  const breakdownRef = useRef(null);
  const openBreakdown = useCallback(() => {
    setBalanceOpen(true);
    // After the frame that mounts the panel, or the scroll targets the
    // collapsed height and lands short of what it just opened.
    requestAnimationFrame(() => {
      breakdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);
  // OLD: const [credits, setCredits] = useState([]);
  // Removed with BUG 14 — this tab no longer keeps a private copy of the
  // balances; it reads the shared useCreditBalances hook above.
  const [subscription, setSubscription] = useState(null);
  // OLD: const [plans, setPlans] = useState([]);
  // The plans list was fetched here only to resolve the current plan's credit
  // allotment as a fallback for the usage arithmetic. That arithmetic is gone
  // (BUG 12 — usage now comes from real consumption), and PlanSelector fetches
  // its own plans, so this state and its request had no remaining reader.
  const [loading, setLoading] = useState(false);
  // The package currently being purchased, so only that tile shows a spinner.
  // A1 — the sidebar "Manage Plans" button links to /credits?view=plans, so the
  // plans/subscriptions screen opens directly instead of dropping the user on
  // the Credits & Usage view with no indication of where plans live.
  //
  // DERIVED from the URL, with no mirroring state — and that is the fix for
  // "Manage Plans does nothing while I'm already on Credits & Subscriptions".
  //
  // It used to be `useState` seeded from the URL, plus an effect that set it
  // true whenever ?view=plans appeared. Closing the view called setShowPlans
  // (false) and left the query string alone, so the URL still read ?view=plans
  // while the credits screen was showing. Clicking Manage Plans then navigated
  // to the URL the app was already on: location.search never changed, the
  // effect never re-ran, and nothing happened. It worked exactly once per page
  // load, which is why it looked like a dead button rather than a stale URL.
  //
  // With one source of truth that cannot happen: every open and close below is
  // a navigation, so the address bar always describes what is on screen, and
  // the view is linkable and survives a refresh.
  const showPlans = new URLSearchParams(location.search).get('view') === 'plans';
  const openPlans = useCallback(() => navigate(PLANS_PATH), [navigate]);
  const closePlans = useCallback(() => navigate(CREDITS_PATH), [navigate]);

  // Derived from the URL for the same reason `showPlans` is: it arrives as a
  // navigation, so it must survive a refresh and describe what is on screen.
  // Mapped to the target PlanSelector's own links use, so there is one scroll
  // handler rather than a second one for arrivals.
  const plansScrollTarget = new URLSearchParams(location.search).get('scrollTo')
    === PLANS_SCROLL_TARGET
    ? PLAN_OPTION_TARGET.SUBSCRIPTIONS
    : null;

  // Subscription action states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  // WHICH words the one end-the-plan dialog uses. 'downgrade' when it was
  // opened from the Basic card's "Downgrade plan" button, 'cancel' from either
  // Cancel Subscription button. Same confirm handler either way — ending the
  // paid plan at the period end IS the move to Basic — so this changes the
  // words, never the action. See downgradeToBasicDialog.
  const [cancelDialogVariant, setCancelDialogVariant] = useState('cancel');
  const openCancelDialog = useCallback((variant) => {
    setCancelDialogVariant(variant);
    setCancelDialogOpen(true);
  }, []);
  // The package shelf lives behind the Add Credits button rather than sitting
  // permanently in the balance card — one entry point, and the card stays about
  // the balance instead of being half sales shelf.
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);
  // Behind "See what features are included" on the trial line.
  const [trialFeaturesOpen, setTrialFeaturesOpen] = useState(false);
  // Behind "Choose a Plan" while a trial is still running — see
  // PLAN_ACTION_HANDLERS, and TrialOptionsDialog for why it exists.
  const [trialOptionsOpen, setTrialOptionsOpen] = useState(false);
  // Behind "See what features are available" on the Basic plan line — the
  // counterpart to the trial's link above.
  const [basicFeaturesOpen, setBasicFeaturesOpen] = useState(false);

  // Settings deep-links here with ?addCredits=1 when it tells a free-plan
  // account how to get credits without subscribing (see BUY_CREDITS_PATH).
  // Same shape as SupportTickets' ?newTicket=1 handler.
  //
  // Unlike ?view=plans above, this SETS state rather than deriving the dialog
  // from the query: the picker must be closable without a navigation, and
  // deriving it would make the close button fight the address bar.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('addCredits') === '1') setAddCreditsOpen(true);
  }, [location.search]);

  const [actionProcessing, setActionProcessing] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Success/Error dialog states
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successDialogMessage, setSuccessDialogMessage] = useState('');
  // The same message as paragraphs of { text, strong }, when the producer can
  // supply them. Preferred over the plain string because a string can only
  // carry line breaks — see RichMessage. Null falls back to the string, so a
  // producer that has not been updated keeps working.
  const [successDialogParts, setSuccessDialogParts] = useState(null);
  // Tone controls the dialog TITLE only. The dismiss button now reads "OK" in
  // every case — see the note on the dialog itself (BILL-009).
  const [successDialogTone, setSuccessDialogTone] = useState('celebrate');
  // An explicit title, when the generic "Done"/"Success" is too vague to be
  // useful — a cancellation confirmation should say so in its heading rather
  // than make the reader parse the body to find out what just happened.
  // null falls back to the tone-derived title.
  const [successDialogTitle, setSuccessDialogTitle] = useState(null);

  // BUG 7 — Recent Transactions, from the billing-history endpoint.
  const [billingHistory, setBillingHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState('');

  // Load subscription + plan data. Credits are NOT fetched here any more —
  // useCreditBalances owns them (BUG 14), and refreshCredits() re-reads them.
  //
  // OLD: this also did `api.getUserCredits(...)` → setCredits(...), the private
  // copy that let this tab drift from Overview and Websites.
  // Returns the fresh subscription (or undefined if the read failed) as well as
  // storing it, so a caller can CHECK what the server actually did instead of
  // assuming its own request worked — see handleReactivateSubscription.
  const loadCreditData = async () => {
    if (!userData?.id) return undefined;
    setLoading(true);
    try {
      const subscriptionResponse = await api.getUserSubscription(userData.id);
      const fresh = subscriptionResponse.data || null;
      setSubscription(fresh);
      refreshCredits();
      return fresh;
    } catch (error) {
      console.error('Failed to load credit data:', error);
      return undefined; // unknown, which is not the same as "no subscription"
    } finally {
      setLoading(false);
    }
  };

  // BUG 7 — loaded separately from the credit/plan data so a slow or failing
  // Stripe invoice fetch can never delay or break the main page.
  const loadBillingHistory = async () => {
    if (!userData?.id) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await api.getBillingHistory(userData.id);
      setBillingHistory(res?.data || []);
      // BILL-004 — the cursor for the next page, from the server's keyset
      // pagination. Null when this is the last page.
      setHistoryCursor(res?.meta?.next_cursor || null);
    } catch (error) {
      setHistoryError(error.response?.data?.error || 'Please try again in a moment.');
    } finally {
      setHistoryLoading(false);
    }
  };

  // BILL-004 — append the next page. Deliberately additive rather than
  // replacing: the user is reading a list, and swapping its contents underneath
  // them loses their place.
  const loadMoreHistory = async () => {
    if (!userData?.id || !historyCursor || historyLoadingMore) return;
    setHistoryLoadingMore(true);
    try {
      const res = await api.getBillingHistory(userData.id, { cursor: historyCursor });
      setBillingHistory((rows) => [...rows, ...(res?.data || [])]);
      setHistoryCursor(res?.meta?.next_cursor || null);
    } catch (error) {
      setHistoryError(error.response?.data?.error || 'Could not load more transactions.');
    } finally {
      setHistoryLoadingMore(false);
    }
  };

  useEffect(() => {
    loadCreditData();
    loadBillingHistory();
    // Keyed on the id (not the object) so auth-token refreshes on tab focus,
    // which produce a new userData reference, don't re-trigger a full reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  // The effect that used to sync the URL into state is gone with the state.
  // Worth noting what it deliberately did NOT do, since the behaviour is
  // intentionally different now: it only ever OPENED the plans view, so a
  // redirect to a bare /credits left it open. Closing on that redirect is the
  // better answer in every flow that actually exists — a plan change returns
  // to PLANS_PATH and stays on plans, while a credit-pack purchase returns to
  // /credits?payment=success and should land on the credits screen it was
  // started from.

  // BUG 14 — both of these now read the SHARED hook, so this tab cannot
  // disagree with Overview or Websites about how many credits remain.
  //
  // OLD — this tab kept its own `credits` state from its own getUserCredits
  // call and did its own arithmetic, which is how the three tabs came to show
  // different numbers:
  //
  //   const getCreditInfo = (type) => {
  //     const credit = credits.find((c) => c.credit_type === type);
  //     return credit ? credit.amount : 0;
  //   };
  //
  //   const currentPlanId = subscription?.plan_id ?? userData?.plan_id ?? null;
  //   const currentPlan = plans.find((p) => p.id === currentPlanId) || null;
  //   const PLAN_ALLOTMENT_KEY = { content: 'content_credits', images: 'image_credits', keywords: 'keyword_credits' };
  //
  //   const getUsage = (type) => {
  //     const c = credits.find((cr) => cr.credit_type === type) || {};
  //     const includedRemaining = Number(c.included_remaining) || 0;
  //     const purchased = Number(c.purchased_remaining) || 0;
  //     const remaining = c.amount != null ? Number(c.amount) : includedRemaining + purchased;
  //     const allotment = Number(c.included_allotment) || Number(currentPlan?.[PLAN_ALLOTMENT_KEY[type]]) || 0;
  //     const used = Math.max(0, allotment - includedRemaining);   // ← BUG 12: 0 whenever allotment was 0
  //     const pct = allotment > 0 ? Math.min(100, Math.round((used / allotment) * 100)) : 0;
  //     return { allotment, includedRemaining, purchased, used, pct, remaining };
  //   };
  // getCreditInfo (per-type BALANCE) and getUsage (per-type CONSUMPTION) are
  // both gone with the three cards they fed. Neither could tell the truth: the
  // hook returned one pooled figure under all three keys, so both answered the
  // same number whatever they were asked. Real per-action consumption now comes
  // from the `usage` field, which the server computes from user_generations.

  // ── What the trial actually includes ──────────────────────────────────────
  // Was "…and 10 free credits of each type", which was wrong twice over: there
  // are no longer three types (migration 011 pooled them), and the figure was
  // 10 from a pricing model two changes ago. The number is READ, not typed —
  // usage.allotment is the iseo row's included_allotment, which follows
  // ISEO_GRANT_FREE — so it cannot go stale again the way the literal did.
  //
  // The clause is dropped entirely until the allotment has loaded, rather than
  // stating a figure that is briefly wrong.
  const trialAllotment = Number(usage?.allotment) || 0;
  // "all premium AI features" was a claim the sentence could not back up — it
  // named no feature, so the reader had to take it on trust and had nowhere to
  // go to check. The credit figure is the concrete half and now leads alone;
  // the features become somewhere to look (see the link at the render site).
  //
  // The figure stays read from the SERVER's allotment rather than typed in.
  // That is not incidental: the wording this replaces began life as "10 free
  // credits of each type" and survived two changes to the credit model
  // precisely because the number was a literal.
  const trialCopy = trialAllotment > 0
    ? `Your 7-day trial includes ${trialAllotment.toLocaleString()} free credits.`
    // No allotment figure yet — the sentence drops the clause it cannot fill
    // rather than printing "includes 0 free credits", which is both wrong and
    // alarming while the balance request is still in flight.
    : "You're on a 7-day free trial.";

  const getNextBillingDate = () => {
    if (!subscription?.next_billing_date) return null;
    return new Date(subscription.next_billing_date).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  // Authoritative trial end comes from the server (users.trial_ends_at — the
  // 7-day trial started at signup). created_at + 7 is only a display fallback.
  const trialEndDateObj = () => {
    if (userData?.trial_ends_at) return new Date(userData.trial_ends_at);
    if (userData?.created_at) {
      const d = new Date(userData.created_at);
      d.setDate(d.getDate() + 7);
      return d;
    }
    return null;
  };

  const getTrialEndDate = () => {
    const d = trialEndDateObj();
    return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
  };

  // Free-trial expiry: no paid subscription and the (effective) trial end is in the
  // past. Prefer the server's authoritative flag; fall back to a local date compare.
  const trialExpired = !subscription && (
    userData?.trial_expired === true ||
    (() => { const d = trialEndDateObj(); return !!d && d.getTime() < Date.now(); })()
  );

  // A trial that is still RUNNING — no paid subscription, and it has not
  // lapsed. Named once because three things now read it and they must agree:
  // the trial line on the card, the end date beside it, and whether "Choose a
  // Plan" explains what happens next or goes straight to the prices.
  const onTrial = !subscription && !trialExpired;

  // The wording lives in utils/subscriptionState.js with the rest of the
  // subscription-derived copy, and is tested there. It takes `isCancelling`
  // rather than re-deriving it, so the chip and the "PLAN ENDS" label below
  // always describe the same subscription the same way.
  const getResetDaysText = () => derivePlanCycleChip({
    subscription,
    cancelling: isCancelling,
    trialExpired,
    trialEnd: trialEndDateObj(),
  });

  const handlePlanChanged = async (result) => {
    // MERGE (IfrahWorks) — the plan changed, so the AuthContext user record is
    // stale too; refresh both so the header badge updates without a reload.
    const [fresh] = await Promise.all([loadCreditData(), refreshUserData()]);
    loadBillingHistory(); // BUG 7 — the change just produced a new invoice row

    // A plan change onto a subscription that is still set to end is the exact
    // state in the report: upgraded twice, still cancelling. Say so here rather
    // than congratulating the user and leaving the contradiction to the banner.
    const stillEnding = fresh === undefined ? { visible: false } : deriveCancellationBanner(fresh);
    if (stillEnding.visible) {
      const endsOn = stillEnding.accessUntil
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      setSuccessDialogTone('neutral');
      setSuccessDialogTitle(null); // generic heading; see the note on the state
      setSuccessDialogParts(null);
      setSuccessDialogMessage(
        `Your plan changed, but this subscription is still scheduled to end on ${endsOn}. `
        + 'Use Reactivate Subscription to keep it running.',
      );
      setSuccessDialogOpen(true);
      closePlans();
      return;
    }

    // ── A FIRST subscription, started on a card already on file ─────────────
    // PlanSelector flags it. It used to get PlanSelector's own plain dialog
    // stacked over the generic "Success" one below. It now gets the same
    // "Subscription active" confirmation a subscription started on Stripe's
    // page gets (Dashboard's checkout-return handler): plan and renewal date,
    // read back from the subscription just loaded rather than from the card
    // that was clicked.
    if (result?.first_subscription) {
      setSuccessDialogTone('celebrate');
      setSuccessDialogTitle(SUBSCRIPTION_STARTED_TITLE);
      setSuccessDialogMessage('');
      setSuccessDialogParts(subscriptionStartedMessage({
        planName: fresh?.plan_name,
        planSlug: fresh?.plan_slug,
        nextBillingDate: fresh?.next_billing_date,
      }));
      setSuccessDialogOpen(true);
      closePlans();
      return;
    }

    // A DOWNGRADE is not a success. `immediate` is the server's own flag — true
    // for an upgrade, false for a change scheduled at the period end — so this
    // needs no second opinion about which direction the plan moved. Without the
    // explicit title the heading falls back to "Success", which is the wrong
    // word for choosing a smaller plan and reads as sarcasm next to a message
    // about losing your allowance.
    const scheduledDowngrade = result.immediate === false;
    setSuccessDialogTone(scheduledDowngrade ? 'neutral' : 'celebrate');
    setSuccessDialogTitle(scheduledDowngrade ? DOWNGRADE_SCHEDULED_TITLE : null);
    if (result.message) {
      // A downgrade arrives with paragraphs so "No charge today." can be
      // emphasised and the lines can breathe; an upgrade sends a string.
      setSuccessDialogParts(Array.isArray(result.message_parts) ? result.message_parts : null);
      setSuccessDialogMessage(result.message);
    } else if (result.immediate) {
      const displayName = formatDisplayPlanName(result.new_plan, result.new_plan_slug);
      setSuccessDialogParts(null);
      setSuccessDialogMessage(`Subscription upgraded${displayName ? ` to ${displayName}` : ''} successfully!`);
    } else {
      const effectiveDate = result.effective_date
        ? new Date(result.effective_date).toLocaleDateString()
        : 'the end of your billing period';
      const displayName = formatDisplayPlanName(result.new_plan, result.new_plan_slug);
      setSuccessDialogParts(null);
      setSuccessDialogMessage(`Subscription change${displayName ? ` to ${displayName}` : ''} scheduled for ${effectiveDate}.`);
    }
    setSuccessDialogOpen(true);
    closePlans();
  };

  const handleCancelSubscription = async () => {
    if (!userData?.id) return;
    setActionProcessing(true);
    setActionError(null);
    try {
      await api.cancelSubscription(userData.id);
      setCancelDialogOpen(false);
      await Promise.all([loadCreditData(), refreshUserData()]);
      // Was a single line naming only the end date. It answered none of what
      // someone actually worries about at this moment: whether access stops
      // now, what happens to credits already paid for, and whether it can be
      // undone. See utils/subscriptionCopy.
      // Headed the way the dialog that confirmed it was: someone who pressed
      // "Confirm downgrade" is told the downgrade is scheduled, not that a
      // cancellation they never named was confirmed. The body already says
      // they move to the Basic plan, so it serves both.
      // OLD: setSuccessDialogTitle(CANCELLATION_CONFIRMED_TITLE);
      setSuccessDialogTitle(cancelDialogVariant === 'downgrade'
        ? DOWNGRADE_SCHEDULED_TITLE
        : CANCELLATION_CONFIRMED_TITLE);
      // Paragraphs, so the lines can breathe and the date access ends can be
      // picked out — see RichMessage.
      setSuccessDialogParts(cancellationConfirmedMessage({
        planName: planName,
        accessUntil: subscription?.next_billing_date,
      }));
      setSuccessDialogTone('neutral'); // a cancellation is not a success
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      setActionError(error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setActionProcessing(false);
    }
  };

  // BUG 10 — undo a scheduled cancellation. Reuses change-plan with the SAME
  // plan id; the server recognises that combination as a reactivation, clears
  // cancel_at_period_end in Stripe and the DB, and charges nothing.
  // loadCreditData() then refetches the subscription, so the "Cancellation
  // Scheduled" banner and the "Plan ends" label both revert on their own.
  const handleReactivateSubscription = async () => {
    if (!userData?.id || !subscription?.plan_id) return;
    setActionProcessing(true);
    setActionError(null);
    try {
      await api.changePlan(userData.id, subscription.plan_id);
      const fresh = await loadCreditData();
      loadBillingHistory();

      // VERIFY, don't assume. The reported bug survived a reactivation and two
      // upgrades: the request returned 200 each time while the subscription
      // stayed set to end, and the CMS announced success regardless — so the
      // user only discovered it from a banner contradicting the dialog they
      // had just dismissed. `fresh === undefined` means the re-read failed, in
      // which case we say nothing rather than invent a failure.
      const stillEnding = fresh !== undefined && deriveCancellationBanner(fresh).visible;
      if (stillEnding) {
        setActionError(
          'We asked to reactivate your subscription, but it is still scheduled to end. '
          + 'Nothing was charged. Please try again, and contact support if it persists.',
        );
        return;
      }

      // Built here rather than echoing the server's `result.message`: the plan
      // name, the next billing date and the credit balance are all already
      // loaded on this page, and the server's generic sentence named none of
      // them. `fresh` is the subscription just re-read above, so the date
      // quoted is the post-reactivation one.
      setSuccessDialogTone('celebrate');
      setSuccessDialogTitle(REACTIVATION_CONFIRMED_TITLE);
      setSuccessDialogParts(reactivationConfirmedMessage({
        planName: fresh?.plan_name || planName,
        nextBillingDate: fresh?.next_billing_date || subscription?.next_billing_date,
        credits: creditsTotal,
      }));
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
      setActionError(error.response?.data?.error || 'Failed to reactivate subscription. Please try again.');
    } finally {
      setActionProcessing(false);
    }
  };

  // CSV export of the billing history currently loaded. Self-contained (no new
  // endpoint, no dependency) and gives the user something they can hand to an
  // accountant. Stripe's own receipt URLs are included as a column so the
  // authoritative documents stay one click away.
  // Named once rather than repeated at every prop that depends on it: the
  // enabled/disabled styling and the click handler have to agree, and five
  // copies of the same expression is how they stop agreeing.
  const canDownloadHistory = billingHistory.length > 0 && !historyLoading;

  const handleDownloadHistory = () => {
    if (billingHistory.length === 0) return;
    // Quote every field and double any embedded quotes — descriptions come from
    // Stripe and can legitimately contain commas.
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Date', 'Description', 'Amount', 'Credits', 'Status', 'Receipt'],
      ...billingHistory.map((tx) => [
        tx.date ? new Date(tx.date).toISOString().slice(0, 10) : '',
        tx.description,
        // Was `tx.type === 'invoice' ? … : ''`, and the server never sends
        // that type — so both columns came out empty on every row. These read
        // the row itself instead of an enum the client had to keep in step.
        activityAmount(tx),
        activityCredits(tx),
        tx.status || '',
        tx.receipt_url || '',
      ]),
    ];
    const csv = rows.map((r) => r.map(esc).join(',')).join('\r\n');
    // BOM so Excel reads UTF-8 correctly.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Renamed with the button — a file called billing-history contradicts the
    // control that produced it.
    a.download = `improveseo-account-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Buying credits ────────────────────────────────────────────────────────
  //
  // Now keyed on a PACKAGE id ('small' | 'medium' | 'large') rather than the
  // old (creditType, quantity: 10) pair. Those three buttons looked like three
  // products but were one: every credit_type maps onto the single ISEO pool, so
  // "10 image credits" and "10 content credits" were the same purchase. The
  // quantity was then rounded server-side to the nearest package, which is why
  // the old buttons could not state a price. Packages are the real unit of sale
  // and carry their own, so the customer sees what they will be charged.
  const startCreditPurchase = async (packageId, paymentMethodId = null) => {
    try {
      const successUrl = `${window.location.origin}/credits?payment=success&type=credits`;
      const cancelUrl = `${window.location.origin}/credits?payment=cancelled`;
      const result = await api.buyCreditPackage(
        userData.id, packageId, successUrl, cancelUrl, paymentMethodId,
      );
      // A subscriber paying on a saved card is charged server-side and gets no
      // URL back — the dialog closes and the balance refreshes in place. A URL
      // means Checkout: a free-plan account, or a card that needed 3-D Secure,
      // which Stripe handles on its own page.
      if (result.success && result.data?.url) {
        // See PENDING_CREDIT_PURCHASE_KEY's doc comment — a same-origin
        // backstop for telling Dashboard this is a credits redirect, in case
        // `type=credits` on successUrl above does not survive the backend's
        // own URL handling.
        try { sessionStorage.setItem(PENDING_CREDIT_PURCHASE_KEY, '1'); } catch { /* best-effort */ }
        window.location.href = result.data.url;
      }
      // Returned so the dialog can tell those two apart.
      return result;
    } catch (error) {
      console.error('Failed to buy credits:', error);
      // ── Say WHY, when the server said why ─────────────────────────────────
      // Every failure used to read "Failed to start purchase. Please try
      // again." — including a deliberate refusal the customer could act on,
      // which is how the billing-address rule stayed invisible to QA for days.
      // A 4xx that carries the server's own sentence shows that sentence;
      // anything else (network, 5xx, no message) keeps the generic line.
      // OLD: setErrorDialogMessage('Failed to start purchase. Please try again.');
      const status = error?.response?.status;
      const serverMessage = error?.response?.data?.error;
      setErrorDialogMessage(
        status >= 400 && status < 500 && typeof serverMessage === 'string' && serverMessage.trim()
          ? serverMessage
          : 'Failed to start purchase. Please try again.',
      );
      setErrorDialogOpen(true);
      // NOT rethrown. The error dialog above already reports this, and the pack
      // dialog surfaces anything it catches inline beside the card — letting
      // this through would report one failure in two places at once. Returning
      // undefined leaves the pack dialog open with the selection intact, which
      // is where the user wants to be after a failed purchase.
    }
    return null;
  };

  const handleBuyCredits = async (packageId, paymentMethodId = null) => {
    if (!userData?.id) return null;
    return startCreditPurchase(packageId, paymentMethodId);
  };

  /**
   * A credit pack was charged without leaving the page.
   *
   * Only the saved-card path reaches here — a Checkout purchase navigates away
   * and comes back through /credits?payment=success. The balance is re-read
   * because the credits are already granted server-side, and the ledger because
   * the purchase has just written a row to it.
   */
  const handleCreditsPurchased = (purchase) => {
    refreshCredits();
    loadBillingHistory();

    // ── Say that it worked ──────────────────────────────────────────────────
    // Without this the dialog simply closed and a number changed, leaving the
    // customer to infer from the balance that they had been charged. The
    // Checkout path has confirmed the purchase all along — the redirect back
    // gives it one — so the two ways of buying the same pack behaved
    // differently, and the one that takes money without leaving the page was
    // the silent one.
    setSuccessDialogTone('celebrate');
    setSuccessDialogTitle(CREDIT_PURCHASE_CONFIRMED_TITLE);
    setSuccessDialogMessage('');
    setSuccessDialogParts(creditPurchaseConfirmedMessage({
      credits: purchase?.quantity,
      amountCents: purchase?.amount,
      // From the server, read back after the grant — see the copy helper for
      // why this is not computed from the balance the page was holding.
      balanceAfter: purchase?.balance_after,
      currency: purchase?.currency,
      // The validity line compares the pack with plan credits, so it is said
      // only to an account that has them. This saved-card path is the
      // subscriber's, but the answer is read rather than assumed.
      onPaidPlan: isLiveSubscription(subscription),
    }));
    setSuccessDialogOpen(true);
  };

  /**
   * Every dialog that must exist in BOTH render trees, as one unit.
   *
   * ── WHY THIS IS A FUNCTION AND NOT INLINE JSX ───────────────────────────
   * A purchase can now start from TWO places: the Add Credits button on the
   * balance card, and the "Buy credit packs" link under the plan tiers. Those
   * live in different render trees, because the plans view is an early return
   * (`if (showPlans) return …`) rather than a branch inside one tree.
   *
   * Every one has to be mounted wherever a purchase can BEGIN, and the ones
   * that are not the picker are what make this more than cosmetic:
   * handleBuyCredits reports failures through the error dialog and missing
   * addresses through the gate, and the success dialog is the only thing that
   * says the purchase worked. Mount the picker without them and a decline, a
   * missing address, or a completed payment all look identical — the dialog
   * simply closes.
   *
   * The success dialog is here for that last reason. It also serves plan
   * changes, cancellation and reactivation, which happen in the main view only;
   * rendering it in both trees costs nothing and removes the trap where a
   * purchase started from the plans view sets state on a dialog that is not on
   * screen.
   *
   * Defined once and called from both returns, so the two can never drift.
   */
  const renderSharedDialogs = () => {
    // The Downgrade button's words, or null for the cancel wording. Built from
    // the same plan name and period end the rest of this page shows, so the
    // dialog cannot name a different plan or day from the card behind it.
    const downgradeCopy = cancelDialogVariant === 'downgrade'
      ? downgradeToBasicDialog({ planName, effectiveDate: subscription?.next_billing_date })
      : null;
    return (
      <>
        {/* ── Buy credit packs ──
            The packs used to sit always-visible inside the balance card, which
            gave the balance and the shop equal billing on the one panel people
            open to read a number. See CreditPackDialog for why choosing and
            paying are now separate steps. */}
        <CreditPackDialog
          open={addCreditsOpen}
          onClose={() => setAddCreditsOpen(false)}
          userId={userData?.id}
          subscription={subscription}
          packages={packages}
          // OLD: planCredits={balance?.plan}
          // REQUESTED: the subtitle names the account's existing credit
          // balance — the Total Credits Remaining figure on this page.
          balanceCredits={creditsTotal}
          onPurchase={handleBuyCredits}
          onPurchased={handleCreditsPurchased}
        />

        {/* ── What the free trial includes ──
            In the shared set for the same reason the two below are: the plans
            view is its own render tree, and a dialog mounted only in the credits
            return is a dead control anywhere else. */}
        <TrialFeaturesDialog
          open={trialFeaturesOpen}
          onClose={() => setTrialFeaturesOpen(false)}
          credits={trialAllotment}
          // The account's OWN limit, from /users/by-email — the same field the
          // Websites tab reads and the server enforces, so this dialog cannot
          // promise a number the product then refuses.
          websiteLimit={userData?.website_limit}
        />

        {/* ── What happens after the trial, and the two ways forward ──
            Opened by "Choose a Plan" while the trial is live. In the shared set
            with the dialog above it, and for the same reason: the plans view is
            its own render tree, and the two buttons in here have to keep working
            from either one. */}
        <TrialOptionsDialog
          open={trialOptionsOpen}
          onClose={() => setTrialOptionsOpen(false)}
          // The formatted date the rest of the card shows, so the dialog cannot
          // name a different day from the line that opened it. Null rather than
          // the 'N/A' placeholder: the copy drops the clause instead of printing
          // a hole in a sentence about money.
          trialEndsOn={trialEndDateObj() ? getTrialEndDate() : null}
          // Lands on Manage Plan AND scrolls to the three paid cards. The dialog
          // has just made the case the options card at the top makes, so opening
          // at the top would repeat it back to someone who has already chosen.
          onGetSubscription={() => {
            setTrialOptionsOpen(false);
            navigate(PLANS_SCROLLED_PATH);
          }}
          // The SAME picker the balance card's Add Credits button opens, and the
          // one PlanSelector's Basic card opens — one purchase flow, three
          // entry points.
          onBuyCreditPack={() => {
            setTrialOptionsOpen(false);
            setAddCreditsOpen(true);
          }}
        />

        {/* ── What the Basic plan includes ──
            The counterpart to the trial dialog above, for the plan a trial lands
            on. Same shared set, same reason: both views have to be able to open
            it. `packages` is passed because the first row of that list quotes
            live pack prices — see BasicFeaturesDialog. */}
        <BasicFeaturesDialog
          open={basicFeaturesOpen}
          onClose={() => setBasicFeaturesOpen(false)}
          packages={packages || []}
        />

        {/* ── Cancel Dialog ──
            Lives HERE, in the shared set, rather than in the Credits-page return
            alone: the plans view is its own render tree, and Manage Plan now
            offers Cancel under the current plan's card. Left where it was, that
            button would set cancelDialogOpen and nothing would appear — the same
            dead-control the Add Credits link hit before it was moved here. */}
        {/* Two wordings, one action. REQUESTED: the Basic card's "Downgrade plan"
            button opened this dialog headed "Cancel Subscription" and asking
            whether to cancel — the question behind a different button. That
            button now gets its own words (downgradeToBasicDialog); both Cancel
            Subscription buttons keep the ones below, which match their labels. */}
        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
          <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>
            {downgradeCopy ? downgradeCopy.title : 'Cancel Subscription'}
          </DialogTitle>
          <DialogContent>
            {downgradeCopy ? (
              <RichMessage paragraphs={downgradeCopy.paragraphs} color="text.secondary" />
            ) : (
              <DialogContentText>
                Are you sure you want to cancel your subscription?
                <br /><br />
                Your subscription will remain active until <strong>{getNextBillingDate()}</strong>, and
                you'll continue to have access to all features until then.
                <br /><br />
                After that, your account will revert to the Basic plan.
              </DialogContentText>
            )}
            {actionError && <Alert severity="error" sx={{ mt: 2, borderRadius: '12px' }}>{actionError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
            <Button onClick={() => setCancelDialogOpen(false)} disabled={actionProcessing} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
              {downgradeCopy ? downgradeCopy.keepLabel : 'Keep Subscription'}
            </Button>
            <Button onClick={handleCancelSubscription} color="error" disabled={actionProcessing} variant="contained" sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
              {actionProcessing
                ? <CircularProgress size={18} />
                : (downgradeCopy ? downgradeCopy.confirmLabel : 'Cancel Subscription')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Error Dialog ── */}
        <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
          <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, color: '#DC2626' }}>Error</DialogTitle>
          <DialogContent>
            <DialogContentText>{errorDialogMessage}</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={() => setErrorDialogOpen(false)} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>OK</Button>
          </DialogActions>
        </Dialog>

        {/* ── Billing address gate ──
            Credits cannot be bought without an address on file. Collected here,
            and the top-up continues the moment it is saved. */}
        <SuccessDialog
          open={successDialogOpen}
          onClose={() => setSuccessDialogOpen(false)}
          title={successDialogTitle}
          tone={successDialogTone}
          message={successDialogMessage}
          parts={successDialogParts}
        />
      </>
    );
  };

  // ── What each action COSTS, from the live price table ────────────────────
  // `pricing` comes from the server via useCreditBalances; DEFAULT_PRICING is
  // only the shape used until that resolves.
  const priceTable = pricing || DEFAULT_PRICING;
  const contentCostMin = Number(priceTable?.content?.small) || DEFAULT_PRICING.content.small;
  const contentCostMax = Number(priceTable?.content?.large) || DEFAULT_PRICING.content.large;
  const imageCost = Number(priceTable?.image) || DEFAULT_PRICING.image;
  const contentCostRange = `${contentCostMin} to ${contentCostMax}`;

  // The per-action capacity figures that used to live here (contentCapacity,
  // imageCapacity, keywordCapacity) went with the three cards they fed. What a
  // credit buys is now stated once, in the tooltip on the balance card, rather
  // than re-derived into three headline numbers that a reader could mistake for
  // three separate allowances.
  // BUG 15 — same helper Overview and Websites use, so the three tabs can never
  // translate the same balance into different numbers.
  // One helper decides the wording for all three cases — none, exactly one, or
  // many — so the Credits page, Overview and Websites cannot word it three ways.
  const creditSummary = creditsSummaryMessage(creditsTotal, creditPieces, pricing);
  // "Trial Ended" is not the name of a plan. When the trial runs out the
  // account moves to Free automatically, so that is what this heading — which
  // sits under the words ACTIVE PLAN — has to say.
  const planName = subscription?.plan_name
    ? formatDisplayPlanName(subscription.plan_name, subscription.plan_slug)
    : (trialExpired ? 'Basic Plan' : 'Free Trial');
  // OLD: const isActivePlan = subscription?.status === 'active';
  //      const isCancelled  = subscription?.status === 'cancelled';
  //
  // Both are gone. "Is a paid plan in force?" is NOT `status === 'active'`: a
  // subscription bought during a trial sits in `trialing`, so that comparison
  // answered "no" for a paying customer. Everything gated on it misfired at
  // once — the chip said FREE TRIAL on a Scale plan, the Cancel button vanished
  // and Manage Plans appeared next to Reactivate. isLiveSubscription answers it
  // for all three, in one place, tested.

  // BILL-005 — one derived answer for "this subscription is winding down",
  // computed from the LIVE subscription payload by the shared helper rather
  // than from any local state set at cancel time.
  //
  // The bare `!!subscription?.cancel_at_period_end` this replaces was already
  // deriving rather than storing, but it showed the banner in two states where
  // it is wrong: a subscription that has already ended (status cancelled), and
  // one whose end date has passed while the deletion webhook is in flight —
  // where it rendered "Access continues until [a date in the past]".
  const cancellationBanner = deriveCancellationBanner(subscription);
  const isCancelling = cancellationBanner.visible;

  // The same shared rules decide the scheduled-change notice, the status chip
  // and which buttons appear, so this card cannot contradict itself the way the
  // report showed — a Scale plan, labelled FREE TRIAL, ending on the same day
  // it was supposedly changing to Grow.
  const pendingChange = derivePendingChange(subscription);
  const statusChip = deriveStatusChip(subscription, { trialExpired });
  // `trialExpired` IS the Basic state — no subscription, and the trial behind
  // it has run out. Passed in rather than re-derived there, so the buttons, the
  // status chip and the date label cannot disagree about which state this is.
  const planActions = derivePlanActions(subscription, undefined, {
    onBasicPlan: trialExpired,
  });
  // Keyed by action, so the list decides WHAT is offered and this decides what
  // each one does — neither has to know the other's ordering.
  const PLAN_ACTION_HANDLERS = {
    // ── "Choose a Plan", on a RUNNING trial, explains first ────────────────
    // REQUESTED. Everywhere else this button goes straight to the grid, and
    // that is still right: an account whose trial has ended, or that is on
    // Basic, has already met the thing the dialog explains. On a live trial it
    // has not, and sending someone to three prices without telling them their
    // work is safe when the trial ends is asking for a decision on a false
    // premise. `onTrial` is the same rule the rest of this card reads.
    choose: onTrial ? () => setTrialOptionsOpen(true) : openPlans,
    // The Basic plan's pair. Both go where the same words go elsewhere on this
    // screen: the plans view, and the one pack picker this component owns.
    // REQUESTED: "Get subscription" lands on the three paid plans, scrolled
    // to them — the same landing as the trial dialog's button of that name.
    // OLD: subscribe: openPlans,
    subscribe: () => navigate(PLANS_SCROLLED_PATH),
    buy_packs: () => setAddCreditsOpen(true),
    manage: openPlans,
    reactivate: handleReactivateSubscription,
    // OLD: cancel: () => setCancelDialogOpen(true),
    cancel: () => openCancelDialog('cancel'),
  };

  const billingOrTrialDate = subscription ? getNextBillingDate() : getTrialEndDate();
  // BUG 9 — a cancelling subscription is not billing again; it ends.
  // OLD: const billingLabel = subscription ? 'NEXT BILLING' : (...);
  const billingLabel = subscription
    ? (isCancelling ? 'PLAN ENDS' : 'NEXT BILLING')
    // Not 'TRIAL ENDED'. The date is worth keeping — it is the only place on
    // the page that says WHEN — but the label above it was one of two badges
    // re-announcing an event the sentence beside them already states. Naming
    // the plan the account is on now says something new, and says it in the
    // present tense.
    : (trialExpired ? 'ON BASIC PLAN SINCE' : 'TRIAL ENDS');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 12 }}>
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );
  }

  // ── Plan Selector view ─────────────────────────────────────────────────
  if (showPlans) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={closePlans}
            sx={{
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              color: TEAL,
              '&:hover': { bgcolor: TEAL_LIGHT },
              borderRadius: '10px',
              px: 2,
              py: 0.75,
            }}
          >
            Back to Credits
          </Button>
        </Box>
        <PlanSelector
          userId={userData?.id}
          currentSubscription={subscription}
          onPlanChanged={handlePlanChanged}
          onTabChange={onTabChange}
          // The pay-as-you-go route out of this screen. It opens the SAME
          // picker the balance card's Add Credits button does — one purchase
          // flow, two entry points — which is why the opener is passed down
          // rather than the dialog rebuilt inside PlanSelector.
          onBuyCreditPacks={() => setAddCreditsOpen(true)}
          // Optional-chained like the Add Credits button below: `packages`
          // is undefined until the price request resolves.
          creditPacksReady={Boolean(packages?.length)}
          // The Basic card quotes real pack prices, so it needs the packages
          // themselves and not just whether any arrived.
          packages={packages || []}
          // Cancelling used to be reachable only from the Credits page, so
          // someone who came here to reconsider had to go back to find it.
          // Same dialog, same handler as the button there — passed down for the
          // same reason onBuyCreditPacks is, rather than rebuilt inside
          // PlanSelector where it could drift.
          // OLD: onCancelPlan={() => setCancelDialogOpen(true)}
          onCancelPlan={() => openCancelDialog('cancel')}
          // The Basic card's "Downgrade plan": the same dialog and handler as
          // Cancel, in the downgrade wording — see cancelDialogVariant.
          onDowngradeToBasic={() => openCancelDialog('downgrade')}
          // Set only when a caller arrived with the choice already made — see
          // PLANS_SCROLLED_PATH, which "Get subscription" navigates to.
          scrollTo={plansScrollTarget}
          // The same `trialExpired` the status chip and plan actions read, so
          // the Basic card says "Active plan" exactly when this page says Basic.
          onBasicPlan={trialExpired}
        />
        {/* Mounted here too: the plans view is its own render tree, so without
            this the link above would open a dialog that does not exist. The
            cancel dialog is in this set for exactly that reason — see the
            note on it. */}
        {renderSharedDialogs()}
      </Box>
    );
  }

  // ── Main Credits & Subscriptions view ──────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

      {/* ── Alerts ──
          The scheduled-change notice is now derived (see derivePendingChange):
          it stays hidden for a change to the plan already held, for a date that
          has passed, and — the case in the report — while a cancellation is
          scheduled, because the subscription ends before the change could run.
          OLD: {subscription?.pending_plan_id && ( ... )} */}
      {pendingChange.visible && (
        <Alert severity="info" sx={{ borderRadius: '12px' }}>
          {/* Shared with Manage Plans and Settings — all three used to phrase
              this differently about the same subscription. */}
          <strong>{PENDING_CHANGE_LABEL}</strong>{' '}
          {pendingChangeMessage(pendingChange, { planDisplayName: formatDisplayPlanName })}
        </Alert>
      )}
      {/* BUG 10 (frontend half) — driven purely by the LIVE subscription, never
          by a local "user just cancelled" flag. loadCreditData() refetches the
          subscription after every plan change, so once the backend clears
          cancel_at_period_end on resubscribe (see the change-plan and
          subscribe-to-plan handlers) this banner disappears on its own.
          OLD: {subscription?.cancel_at_period_end && ( ... )} */}
      {isCancelling && (
        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
          {/* Shared with Manage Plan — the sentence, the plan name and the date
              are composed in one place so this banner cannot say something
              different from the grid the Manage Plan button opens, or from the
              confirmation dialog shown at the moment of cancelling. */}
          <strong>{CANCELLATION_SCHEDULED_LABEL}</strong>{' '}
          {cancellationScheduledMessage(cancellationBanner, {
            planName: subscription?.plan_name,
            // The slug is passed as well, so this names the plan exactly the
            // way the header above it does (see `planName`) — mapping on the
            // name alone is the weaker of the two routes.
            planDisplayName: (name) => formatDisplayPlanName(name, subscription?.plan_slug),
          })}
        </Alert>
      )}
      {/* actionError was only rendered inside the cancel dialog, so a failure
          from the Reactivate button (which has no dialog) would have been
          silent. Shown here whenever that dialog isn't the thing reporting it. */}
      {actionError && !cancelDialogOpen && (
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ borderRadius: '12px' }}>
          {actionError}
        </Alert>
      )}

      {/* ── Page Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          {/* BUG 2 — heading renamed to match the sidebar label.
              OLD: Credits & Usage */}
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 32, lineHeight: 1.1, letterSpacing: '-0.02em', color: TEXT }}>
            Credits & Subscriptions
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 15, color: TEXT_MUTED, mt: 0.75, maxWidth: 520 }}>
            Manage your content generation resources across all your connected websites.
          </Typography>
        </Box>
        {/* Next reset badge. Absent, not empty, when there is no next reset to
            name — an account whose trial has ended has no reset date, and the
            badge used to fill the gap by repeating that the trial had ended. */}
        {getResetDaysText() && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#E8F3F1', border: '1px solid #C0DDD9', borderRadius: '9999px', px: 2, py: 0.75, alignSelf: 'flex-start', mt: 0.5 }}>
            <ClockIcon sx={{ fontSize: 15, color: TEAL }} />
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: TEAL }}>
              {getResetDaysText()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Active Plan Card ── */}
      <Box sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid #E5E7E0',
        borderRadius: '16px',
        p: { xs: '24px', md: '28px 32px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
        flexWrap: 'wrap',
        boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.25)',
      }}>
        {/* Left: icon + plan info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '14px',
            background: ORANGE_GRADIENT,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <BadgeIcon sx={{ fontSize: 28, color: '#FFFFFF' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: ORANGE }}>
                ACTIVE PLAN
              </Typography>
              {/* The chain this replaces ended in 'FREE TRIAL', so any status
                  outside {active, cancelled} — `trialing` above all — labelled
                  a paid plan a free trial. deriveStatusChip cannot: with a
                  subscription present it never returns trial wording.
                  OLD: {isActivePlan ? 'ACTIVE' : isCancelled ? 'CANCELLED'
                        : trialExpired ? 'TRIAL ENDED' : 'FREE TRIAL'} */}
              {/* Three tones, not two. The old positive/else pair sent every
                  non-positive status to orange, which is right for one that
                  wants attention and wrong for 'muted' — the Basic plan, which
                  is a plan the account is legitimately on rather than a fault
                  to flag. Grey says "this is your state" without alarm. */}
              <Box sx={{ bgcolor: STATUS_CHIP_TONES[statusChip.tone]?.bg || ORANGE_LIGHT, borderRadius: '9999px', px: 1.25, py: '2px' }}>
                {/* The shared label is sentence case; the capitals on this card
                    are styling, not different words from the ones Settings
                    shows for the same subscription. */}
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: STATUS_CHIP_TONES[statusChip.tone]?.color || ORANGE }}>
                  {statusChip.label}
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20, color: TEXT, lineHeight: 1.2 }}>
              {planName}
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, color: TEXT_MUTED, mt: 0.25 }}>
              {/* The free-plan line is the SHARED one — see freePlanNextSteps.
                  Settings renders the same words; this card and that block had
                  already drifted apart once, which is why the sentence is no
                  longer written at either call site.

                  The links are in-page here rather than navigations: this card
                  sits on the very screen Settings would send someone to, and
                  the pack picker is a dialog this component owns. Same words,
                  same destinations, one fewer round trip. */}
              {subscription
                // "Full access" belongs to the top tier only — see
                // activePlanSubtitle. It used to be printed on every paid plan,
                // telling a Grow customer they already had everything, on a card
                // that offers them an upgrade.
                ? activePlanSubtitle({
                  planName: subscription.plan_name,
                  planSlug: subscription.plan_slug,
                })
                : trialExpired
                  ? (
                    <>
                      {freePlanNextSteps().map((seg, i) => (seg.action ? (
                        <Box
                          key={seg.text}
                          component="button"
                          type="button"
                          onClick={seg.action === FREE_PLAN_ACTION.PLANS
                            ? openPlans
                            : () => setAddCreditsOpen(true)}
                          sx={{
                            p: 0,
                            border: 0,
                            bgcolor: 'transparent',
                            font: 'inherit',
                            fontWeight: 700,
                            color: TEAL,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textUnderlineOffset: '2px',
                            '&:hover': { color: '#156874' },
                          }}
                        >
                          {seg.text}
                        </Box>
                      ) : (
                        // eslint-disable-next-line react/no-array-index-key
                        <React.Fragment key={`t${i}`}>{seg.text}</React.Fragment>
                      )))}
                      {' '}
                      {/* REQUESTED: the trial's link, for the plan a trial
                          LANDS on. Same treatment and same position as the one
                          on the trial line, because it answers the same
                          question about a different plan — and Basic is the
                          plan an account spends the longest on while being the
                          one the card described the least. */}
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setBasicFeaturesOpen(true)}
                        sx={{
                          p: 0,
                          border: 0,
                          bgcolor: 'transparent',
                          font: 'inherit',
                          fontWeight: 700,
                          color: TEAL,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                          '&:hover': { color: '#156874' },
                        }}
                      >
                        See what features are available
                      </Box>
                    </>
                  )
                  : (
                    <>
                      {trialCopy}{' '}
                      {/* The features the old sentence merely asserted. A dialog
                          rather than the plan grid: the grid answers "what would
                          I get if I PAID", and the question here is what the
                          trial already includes. It renders the same ticked list
                          the plan cards do — see PlanFeatureList — so the two
                          look alike by construction rather than by resemblance. */}
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setTrialFeaturesOpen(true)}
                        sx={{
                          p: 0,
                          border: 0,
                          bgcolor: 'transparent',
                          font: 'inherit',
                          fontWeight: 700,
                          color: TEAL,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textUnderlineOffset: '2px',
                          '&:hover': { color: '#156874' },
                        }}
                      >
                        See what features are included
                      </Box>
                    </>
                  )}
            </Typography>
          </Box>
        </Box>

        {/* Centre: billing / trial date */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: TEXT_MUTED, mb: 0.5 }}>
            {billingLabel}
          </Typography>
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 20, color: TEXT }}>
            {billingOrTrialDate || '—'}
          </Typography>
        </Box>

        {/* Right: CTA buttons ──
            One map over derivePlanActions instead of three hand-gated buttons.
            The order and the emphasis are decided there, so this cannot render
            them in the wrong sequence or promote the wrong one — and a running
            plan now offers Manage Plan at all, which the old `!live` gate made
            impossible. */}
        {/* Named as a group: it is a set of related choices about one
            subscription, and a screen reader announcing them as three loose
            buttons on a busy page gives no clue they belong together. */}
        <Box
          role="group"
          aria-label="Plan actions"
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexShrink: 0 }}
        >
          {planActions.map((action) => (
            <Button
              key={action.key}
              onClick={PLAN_ACTION_HANDLERS[action.key]}
              disabled={action.key === 'reactivate' && actionProcessing}
              startIcon={action.key === 'cancel' ? <CancelIcon sx={{ fontSize: 15 }} /> : undefined}
              sx={{ ...BUTTON_BASE, ...BUTTON_VARIANTS[action.variant] }}
            >
              {action.key === 'reactivate' && actionProcessing
                ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                : action.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* ── Credits about to lapse ──
          Above the balance card, because the breakdown that carries the expiry
          dates is collapsed by default — without this, the one fact with a
          deadline attached is the one fact hidden behind a click. Same sentence
          as the bell and the Dashboard banner, from one rule.

          No button: the balance it refers to is already on this screen. */}
      <CreditExpiryBanner lots={lots} />

      {/* ── Total remaining + what it's worth ──
          BUG 14 — one number, from the shared hook, identical to Overview and
          Websites. This tab previously showed only the three per-type figures,
          so there was nothing here to reconcile against the other tabs.
          BUG 15 — the same "pieces of SEO content" translation shown on
          Overview and Websites, computed by the shared helper (never
          hardcoded, never duplicated math). */}
      {/* ── Balance and spend, side by side ────────────────────────────────
          Two halves of one question — what is left, and what went. Stacked,
          the second was below the fold on a laptop, so the answer to "where
          did my credits go" sat out of sight beneath the number that prompted
          it. They collapse back to one column below md, where side-by-side
          would squeeze both.

          A grid rather than flex, so the two columns are exactly equal
          regardless of content: the spend card is the taller of the two and
          flex would let the balance card shrink to its own content width.
          Grid's default stretch also gives them matching heights, which is
          what makes them read as a pair rather than two loose panels. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 3,
        alignItems: 'stretch',
      }}>
      <Box sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid #E5E7E0',
        borderRadius: '16px',
        p: { xs: '20px 24px', md: '22px 28px' },
        display: 'flex',
        // flex-start, not center. The height was content-driven while this card
        // was stacked, so centring did nothing; as a grid item it is stretched
        // to match the taller spend card beside it, and centred content would
        // float in the middle of the panel with the label detached from its
        // own number.
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.25)',
      }}>
        <Box sx={{ width: '100%' }}>
          {/* The card's only child is width:100%, so a sibling button would wrap
              to its own line. It goes in the label row instead, which becomes a
              space-between pair. */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: TEXT_MUTED }}>
              Total Credits Remaining
            </Typography>
            {/* The info icon carries the WEIGHTING. Without it the headline is
                an uninterpretable number: actions cost different amounts, so
                "1,139 credits" means nothing until you know an article is 10
                and an image is 5. */}
            <Tooltip
              arrow
              placement="top"
              // NOT disableInteractive. MUI keeps a tooltip open while the
              // pointer travels into it only while that stays false, and this
              // one contains a control — one the pointer cannot reach reads as
              // a broken one.
              slotProps={{
                // Colour is set on the tooltip SLOT rather than per-Typography.
                // The children inherit the app's body colour otherwise, which
                // rendered this copy dark-on-dark; doing it here means a line
                // added inside later cannot miss it.
                tooltip: {
                  sx: {
                    color: '#FFFFFF',
                    bgcolor: 'rgba(28,32,30,0.96)',
                    maxWidth: 320,
                    p: 1.5,
                    // Typography applies its own colour from the theme, so a
                    // colour on this container does NOT reach it — which is why
                    // the previous attempt left the copy dark-on-dark while the
                    // link, coloured explicitly, was the only readable line.
                    // Forcing inherit is what makes the container's colour win,
                    // and it covers any line added here later.
                    '& .MuiTypography-root': { color: 'inherit' },
                    // Same reason, for anything rendered as a link or a button
                    // in here: both carry their own colour from the theme.
                    '& a, & button': { color: '#FFFFFF' },
                  },
                },
                arrow: { sx: { color: 'rgba(28,32,30,0.96)' } },
              }}
              title={
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>What a credit buys</Typography>
                  {/* Rendered FROM the live price table, not typed in. A second
                      copy of the prices here would drift from what is actually
                      charged the first time one moved. */}
                  <Typography sx={{ fontSize: 12 }}>
                    SEO content — {contentCostRange} credits, depending on post size
                  </Typography>
                  <Typography sx={{ fontSize: 12 }}>AI image — {imageCost} credits</Typography>
                  {/* Free, so not a price from the table — but still worth saying
                      here, where people look up what each thing costs. */}
                  <Typography sx={{ fontSize: 12 }}>Keyword lists — free</Typography>
                  <Typography sx={{ fontSize: 12, mt: 1 }}>
                    Re-generating content costs less than the first generation.
                  </Typography>
                  {/* A control, not a route: the answer is the breakdown panel
                      on this same card, so this opens it and scrolls to it. It
                      keeps the underline of the link it replaces because the
                      affordance is what makes it findable inside a tooltip. */}
                  <Box
                    component="button"
                    type="button"
                    onClick={openBreakdown}
                    sx={{
                      display: 'inline-block', mt: 1, p: 0, border: 0, bgcolor: 'transparent',
                      cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
                      color: '#FFFFFF', textAlign: 'left',
                      textDecoration: 'underline',
                      textDecorationColor: 'rgba(255,255,255,0.6)',
                    }}
                  >
                    Which credits are used first →
                  </Box>
                </Box>
              }
            >
              <InfoOutlinedIcon
                aria-label="How credits are used"
                sx={{ fontSize: 15, color: TEXT_MUTED, cursor: 'help' }}
              />
            </Tooltip>
          </Box>

            {/* Disabled rather than hidden while the rate table loads: the
                packages come from the same request as the prices, and a button
                that opens an empty dialog is worse than one that is visibly
                not ready yet. */}
            <Button
              onClick={() => setAddCreditsOpen(true)}
              // Optional-chained: a caller that stubs useCreditBalances without
              // this field (every existing test mock does) must render a
              // disabled button, not crash the page.
              disabled={!packages?.length}
              sx={{
                ...BUTTON_BASE,
                ...BUTTON_VARIANTS.credits,
                fontSize: 13,
                px: 2.5,
                py: 1,
                flexShrink: 0,
                // Outlined, so disabled greys the OUTLINE rather than filling
                // the button — a filled grey block would read as a different
                // control, not the same one unavailable.
                '&.Mui-disabled': {
                  bgcolor: '#FFFFFF',
                  color: TEXT_MUTED,
                  border: '1px solid #DDE0D8',
                  boxShadow: 'none',
                },
              }}
            >
              Add Credits
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 34, lineHeight: 1, color: TEXT }}>
              {creditsTotal !== null ? creditsTotal.toLocaleString() : '—'}
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 14, color: TEXT_MUTED }}>
              {creditsTotal === null
                ? 'Calculating what your balance is worth…'
                : creditSummary}
            </Typography>
          </Box>

          {/* ── The three-row breakdown, collapsed by default ─────────────────
              Collapsed because the headline is the answer to the question
              people actually arrive with. The rows below answer the follow-up —
              "what is that made of, and when does it go?" — and that is the
              ONLY place expiry appears on this page. Warning about impending
              loss is the job of the email and the admin alert, not a banner
              that nags on every visit about something the user cannot act on
              at that moment. */}
          <Box
            component="button"
            type="button"
            ref={breakdownRef}
            onClick={() => setBalanceOpen((v) => !v)}
            aria-expanded={balanceOpen}
            sx={{
              mt: 1.5, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 0.5,
              fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 13, color: TEAL,
            }}
          >
            {/* Closed, the label names what is inside — the plan/purchased
                split AND the expiry dates, which "Show breakdown" did not
                mention at all. Open, it only has to say how to close it. */}
            {balanceOpen ? 'Hide details' : 'Breakdown and expiry dates'}
            <ExpandMoreIcon sx={{ fontSize: 18, transform: balanceOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
          </Box>

          <Collapse in={balanceOpen} unmountOnExit>
            {/* Was three fixed rows — Plan credits, Purchased credits, Next to
                expire — computed from one aggregated balance. That could not
                answer the question this panel is opened for: WHICH credits go,
                and when. "Next to expire" gave a number and a date but never
                said what they belonged to, and the plan figure merged batches
                that lapse a month apart. Now one row per batch, each with its
                own date. See utils/creditBreakdown. */}
            {/* On a RUNNING trial the plan lot is the trial grant, and the
                expiry the server stored on it never arrives — the trial-end
                path zeroes those credits first. Passing the trial end makes
                the row say what actually happens; see creditBreakdown.js.
                Null for a subscriber and for an expired trial, both of which
                keep the ordinary plan wording. */}
            <CreditBreakdown
              lots={lots}
              trialEndsOn={!subscription && !trialExpired ? (userData?.trial_ends_at || null) : null}
            />
          </Collapse>

        </Box>
      </Box>

      {/* ── Credits used this cycle ──
          REPLACES three cards (SEO Content Generation / AI Images / Keyword
          Lists) that each printed the whole pooled balance. With 634 credits
          that read "634" three times, which invites adding them up to 1,902.
          All three also drew the SAME progress bar, because all three were fed
          from one shared object — so the "usage" they showed was identical
          regardless of what had actually been generated.

          Splitting the balance by action was always fiction: there is one pool.
          Splitting the SPEND is real, so that is what this shows. Remaining
          balances stay in the card above (repeating them is the duplication
          being removed) and the per-action Buy Credits buttons go with them —
          purchasing now lives beside the balance it tops up, as a choice of
          package. See CreditPackagePicker. */}
      <CreditUsageCard
        usage={usage}
        total={creditsTotal}
        cycleResetsAt={subscription?.next_billing_date || trialEndDateObj()}
        loading={creditsTotal === null}
      />
      </Box>

      {/* ── Recent Transactions ── */}
      <Box>
        {/* Section header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 22, color: TEXT }}>
            Account Activity
          </Typography>
          {/* BUG 13 — was an inert <Typography> with no click handler: a dead
              link advertising a feature that did nothing. Now exports real
              invoice history as CSV, including Stripe's own receipt URLs.
              When there is nothing to export the control is visibly DISABLED
              with an explanatory tooltip, rather than hidden (which would look
              like the feature had vanished) or silently doing nothing. */}
          <Tooltip
            title={
              historyLoading ? 'Loading your account activity…'
                : billingHistory.length === 0 ? 'Nothing yet — your credit purchases and plan changes will appear here.'
                  : 'Download your account activity as a CSV'
            }
          >
            {/* span wrapper: MUI Tooltip needs a DOM child that still fires
                mouse events when the control itself is disabled */}
            <span>
              {/* role, tabIndex and onKeyDown, because this is a <span>: it
                  looked like a link and behaved like one for a mouse, but it
                  was unreachable by keyboard and invisible to a screen reader,
                  so there was no way at all to export your own billing records
                  without a pointer. A <span> that handles onClick has to carry
                  the rest of the button contract itself. */}
              <Typography
                component="span"
                role="button"
                tabIndex={canDownloadHistory ? 0 : -1}
                aria-disabled={!canDownloadHistory}
                onClick={canDownloadHistory ? handleDownloadHistory : undefined}
                onKeyDown={canDownloadHistory ? (e) => {
                  // Space is what makes it a button rather than a link, and it
                  // must be prevented or the page scrolls instead.
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleDownloadHistory();
                  }
                } : undefined}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: 14,
                  color: canDownloadHistory ? ORANGE : TEXT_MUTED,
                  cursor: canDownloadHistory ? 'pointer' : 'not-allowed',
                  opacity: canDownloadHistory ? 1 : 0.55,
                  '&:hover': canDownloadHistory ? { textDecoration: 'underline' } : {},
                }}
              >
                Download Account Activity
              </Typography>
            </span>
          </Tooltip>
        </Box>

        {/* The transaction table, full width.

            A "CURRENT PLAN / Manage Plans" mini card used to sit in a 220px
            column to its left, which is why this was a grid. Removed by
            request: the plan card at the top of this page already states the
            current plan, and Manage Plans is reachable from there and from the
            sidebar, so the card repeated both without adding anything — while
            costing the activity table a fifth of its width. */}
        <Box>
          {/* Column headers */}
          <Box sx={{
            // Five columns now. 'AMOUNT' used to be a single column that
            // showed money OR credits depending on the row, so a top-up's
            // credits and its price could never be read side by side, and
            // 'STATUS' was replaced by the receipt link whenever one existed
            // — you could see the status or the receipt, never both.
            ...ACTIVITY_GRID,
            pb: 1.5,
            borderBottom: '1px solid #E5E7E0',
          }}>
            {['DATE', 'DESCRIPTION', 'CREDITS', 'AMOUNT', 'STATUS'].map((col, i) => (
              <Typography
                key={col}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: '0.08em',
                  color: TEXT_MUTED,
                  // Numbers right-align so the columns can be compared down
                  // the page; the two text columns stay left.
                  textAlign: i === 2 || i === 3 ? 'right' : 'left',
                }}
              >
                {col}
              </Typography>
            ))}
          </Box>

          {/* BUG 7 — real rows, from /stripe/billing-history (Stripe invoices
              merged with credit purchases and plan grants).
              OLD: a hardcoded "No transactions yet" block, because no
              user-facing transactions endpoint existed — so the list stayed
              empty no matter how many purchases or upgrades were made. */}
          {historyLoading ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <CircularProgress size={22} sx={{ color: TEAL }} />
            </Box>
          ) : historyError ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14, color: TEXT }}>
                Couldn't load your transactions
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 13, color: TEXT_MUTED, mt: 0.5 }}>
                {historyError}
              </Typography>
              <Button onClick={loadBillingHistory} sx={{ mt: 1, textTransform: 'none', fontWeight: 700, fontSize: 13, color: ORANGE }}>
                Try again
              </Button>
            </Box>
          ) : billingHistory.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14, color: TEXT }}>
                No transactions yet
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 13, color: TEXT_MUTED, mt: 0.5 }}>
                Your credit purchases and plan changes will appear here.
              </Typography>
            </Box>
          ) : (
            billingHistory.map((tx, i) => (
              <Box
                key={tx.id}
                data-testid="activity-row"
                sx={{
                  ...ACTIVITY_GRID,
                  alignItems: 'center',
                  py: 1.75,
                  borderBottom: i < billingHistory.length - 1 ? '1px solid #F0F0EA' : 'none',
                }}
              >
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT_MUTED }}>
                  {tx.date ? new Date(tx.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                </Typography>

                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: TEXT, pr: 1 }}>
                  {tx.description}
                </Typography>

                {/* CREDITS — what the row added or removed from the balance.
                    Its own column now: a top-up is a payment AND a grant, and
                    the single shared column could only ever show one of them,
                    pushing the other into a subtitle. */}
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: 13,
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    color: isDecrease(tx) ? '#B45309' : TEXT,
                  }}
                >
                  {hasCredits(tx) ? activityCredits(tx) : '—'}
                </Typography>

                {/* AMOUNT — the money charged, and only ever the money. */}
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: 13,
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    color: TEXT,
                  }}
                >
                  {hasAmount(tx) ? activityAmount(tx) : '—'}
                </Typography>

                {/* STATUS — always shown. It used to be REPLACED by the
                    receipt link, so a paid row could not display both; the
                    receipt now sits under the status rather than instead of
                    it. */}
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 12, color: TEXT_MUTED, textTransform: 'capitalize' }}>
                    {tx.status || '—'}
                  </Typography>
                  {/* The receipt line is ALWAYS laid out, and merely hidden
                      when there is nothing to link to.

                      It used to be rendered conditionally, and because a row's
                      height follows its tallest cell, every row with a receipt
                      stood a line taller than every row without — which is the
                      uneven spacing down the list. Reserving the line keeps the
                      rhythm even, and does it without a magic minHeight that
                      would silently stop matching if either font size moved.

                      aria-hidden and inert, so a screen reader does not
                      announce a "Receipt" that is not there and the keyboard
                      does not stop on it. */}
                  {tx.receipt_url ? (
                    <Typography
                      component="a"
                      href={tx.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={ACTIVITY_RECEIPT_SX}
                    >
                      Receipt
                    </Typography>
                  ) : (
                    <Typography aria-hidden sx={{ ...ACTIVITY_RECEIPT_SX, visibility: 'hidden' }}>
                      Receipt
                    </Typography>
                  )}
                </Box>
              </Box>
            ))
          )}

          {/* BILL-004 — paginated, so a long history is reachable rather
              than silently truncated at the first page. */}
          {historyCursor && !historyLoading && !historyError && (
            <Box sx={{ textAlign: 'center', pt: 2 }}>
              <Button
                onClick={loadMoreHistory}
                disabled={historyLoadingMore}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: 13, color: ORANGE }}
              >
                {historyLoadingMore ? <CircularProgress size={16} sx={{ color: ORANGE }} /> : 'Load more'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {renderSharedDialogs()}



    </Box>
  );
};

export default CreditManagement;
